import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  createProject as createProjectApi,
  deleteProjectById,
  listProjects,
  mapUserRole,
  updateProject as updateProjectApi,
  type ApiProject,
  type ProjectsApiOptions,
} from '../../api/projects';
import { assignInitialMembers } from './assignInitialMembers';
import { defaultProjects } from './mockProjects';
import { Project, ProjectMember } from './types';
import { isForbiddenError } from '../../api/projects';

export const STORAGE_KEY = 'ses-project-ui-preview';
export const PROJECTS_LOAD_ERROR = 'projects-load-failed';
export const PROJECTS_SAVE_ERROR = 'projects-save-failed';
export const PROJECTS_FORBIDDEN_ERROR = 'projects-forbidden';

export const PROJECTS_CHANGED_EVENT = 'ses-projects-changed';

function notifyProjectsChanged(source: symbol) {
  window.dispatchEvent(
    new CustomEvent(PROJECTS_CHANGED_EVENT, { detail: source }),
  );
}

type RefreshOptions = {
  page?: number;
  silent?: boolean;
};

const DEFAULT_ORGANIZATION = 'Acme Retail';

function formatDate(date?: string) {
  return date ? date.slice(0, 10) : '';
}

/**
 * Maps API project response to frontend Project type.
 * Uses real userRole from backend when available.
 */
function mapApiProjectToProject(
  apiProject: ApiProject,
  existing?: Project,
): Project {
  return {
    id: String(apiProject.id),
    name: apiProject.name,
    description: apiProject.description ?? '',
    organization: String(
      apiProject.organizationId ?? existing?.organization ?? '',
    ),
    role: mapUserRole(apiProject.role) ?? existing?.role ?? 'Viewer', // Use real role from backend
    updatedAt: formatDate(apiProject.updatedAt) || existing?.updatedAt || '',
    owner: apiProject.ownerName ?? existing?.owner ?? '',
    members: existing?.members ?? [],
    artifacts: existing?.artifacts ?? [],
    workItems: existing?.workItems ?? [],
  };
}

export function isValidProjectMemberEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function createProjectSlug(name: string) {
  return (
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'project'
  );
}

export function createUniqueProjectId(name: string, projects: Project[]) {
  const slug = createProjectSlug(name);
  const existingIds = new Set(projects.map(project => project.id));
  let candidate = `${slug}-${Date.now()}`;
  let suffix = 1;

  while (existingIds.has(candidate)) {
    suffix += 1;
    candidate = `${slug}-${Date.now()}-${suffix}`;
  }

  return candidate;
}

export function loadProjects(isProjectPreviewMode: boolean): Project[] {
  if (typeof window === 'undefined' || !isProjectPreviewMode) {
    return defaultProjects;
  }

  const stored = window.sessionStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return defaultProjects;
  }

  try {
    const parsed = JSON.parse(stored) as Project[];
    return Array.isArray(parsed) ? parsed : defaultProjects;
  } catch {
    return defaultProjects;
  }
}

export function useProjectStore() {
  const config = useApi(configApiRef);
  const isProjectPreviewMode =
    config.getOptionalBoolean('ses.projectPreviewMode') ?? false;
  const backendUrl = config.getString('backend.baseUrl');
  const projectsProxyPath =
    config.getOptionalString('ses.projectsApi.proxyPath') ??
    '/api/proxy/ses-projects';

  const [projects, setProjects] = useState<Project[]>(() =>
    loadProjects(isProjectPreviewMode),
  );
  const [loading, setLoading] = useState(!isProjectPreviewMode);
  const [error, setError] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const instanceId = useRef(Symbol('project-store')).current;
  const pageRef = useRef(1);
  const fetchCounterRef = useRef(0);

  const setPageSynced = useCallback((p: number) => {
    pageRef.current = p;
    setPage(p);
  }, []);

  const apiOptions = useMemo<ProjectsApiOptions>(
    () => ({
      baseUrl: `${backendUrl}${projectsProxyPath}`,
    }),
    [backendUrl, projectsProxyPath],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && isProjectPreviewMode) {
      const serialized = JSON.stringify(projects);
      if (window.sessionStorage.getItem(STORAGE_KEY) !== serialized) {
        window.sessionStorage.setItem(STORAGE_KEY, serialized);
        notifyProjectsChanged(instanceId);
      }
    }
  }, [instanceId, isProjectPreviewMode, projects]);

  const refreshProjects = useCallback(
    async ({ page: pageNum, silent = false }: RefreshOptions = {}) => {
      if (isProjectPreviewMode) {
        setProjects(loadProjects(true));
        setLoading(false);
        setError(undefined);
        return;
      }

      const fetchId = ++fetchCounterRef.current;
      const currentPage = pageNum ?? pageRef.current;
      // A silent refresh runs while the user is looking at data that is already
      // loaded. Turning on `loading` would make the pages replace themselves
      // with a spinner, and a failure must not replace the data with an error.
      if (!silent) {
        setLoading(true);
        setError(undefined);
      }
      try {
        const response = await listProjects(apiOptions, {
          page: currentPage,
          pageSize,
        });
        if (fetchId !== fetchCounterRef.current) return;
        setProjects(
          (response.projects ?? []).map(apiProject =>
            mapApiProjectToProject(apiProject),
          ),
        );
        setTotal(Number(response.total ?? 0));
        setPageSynced(currentPage);
      } catch (err) {
        if (fetchId !== fetchCounterRef.current) return;
        if (!silent)
          setError(
            isForbiddenError(err)
              ? PROJECTS_FORBIDDEN_ERROR
              : PROJECTS_LOAD_ERROR,
          );
      } finally {
        if (fetchId === fetchCounterRef.current) setLoading(false);
      }
    },
    [apiOptions, isProjectPreviewMode, pageSize, setPageSynced],
  );

  const goToPage = useCallback(
    (newPage: number) => {
      refreshProjects({ page: newPage });
    },
    [refreshProjects],
  );

  const nextPage = useCallback(() => {
    if (page * pageSize < total) {
      goToPage(page + 1);
    }
  }, [page, pageSize, total, goToPage]);

  const prevPage = useCallback(() => {
    if (page > 1) {
      goToPage(page - 1);
    }
  }, [page, goToPage]);

  useEffect(() => {
    if (!isProjectPreviewMode) {
      refreshProjects();
    }
  }, [isProjectPreviewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each page owns its own copy of this state, so a project created in one
  // place (e.g. the sidebar dialog) would otherwise be missing from the others
  // until a full reload.
  useEffect(() => {
    const reloadWhenAnotherInstanceChanged = (event: Event) => {
      if ((event as CustomEvent).detail !== instanceId) {
        refreshProjects({ silent: true });
      }
    };
    window.addEventListener(
      PROJECTS_CHANGED_EVENT,
      reloadWhenAnotherInstanceChanged,
    );
    return () =>
      window.removeEventListener(
        PROJECTS_CHANGED_EVENT,
        reloadWhenAnotherInstanceChanged,
      );
  }, [instanceId, refreshProjects]);

  // Creates the project itself, without members, so the create dialog can
  // call this as soon as a draft member needs an org-membership check and
  // get a real project id to check against (SearchAssignableUsers is
  // permission-gated per project, so there's no way to run that check before
  // a project exists). assignMembers below does the rest once the dialog is
  // ready to finish.
  const createProjectShell = async (input: {
    name: string;
    description: string;
  }): Promise<Project> => {
    if (!isProjectPreviewMode) {
      try {
        setError(undefined);
        const response = await createProjectApi(apiOptions, {
          name: input.name.trim(),
          description: input.description.trim(),
        });
        if (!response.project) {
          throw new Error('Projects API response did not include project');
        }
        const project = mapApiProjectToProject(response.project);
        setProjects(current => [project, ...current]);
        notifyProjectsChanged(instanceId);
        return project;
      } catch (err) {
        setError(PROJECTS_SAVE_ERROR);
        throw err;
      }
    }

    const project: Project = {
      id: createUniqueProjectId(input.name, projects),
      name: input.name.trim(),
      description: input.description.trim(),
      organization: DEFAULT_ORGANIZATION,
      role: 'Owner',
      updatedAt: new Date().toISOString().slice(0, 10),
      // assignMembers (called right after, in the preview flow too) replaces
      // this with the real draft list, self member included.
      members: [],
      artifacts: [],
      workItems: [],
    };

    setProjects(current => [project, ...current]);
    return project;
  };

  // Sets the drafted members' roles on an already-created project (see
  // createProjectShell above) and reports back the ones that could not be
  // added, same as the single combined call used to.
  const assignMembers = async (
    projectId: string,
    members: ProjectMember[],
  ): Promise<ProjectMember[]> => {
    if (!isProjectPreviewMode) {
      const failedMembers = await assignInitialMembers(projectId, members);
      // A drafted member can be given the Owner role here, which really
      // transfers ownership server-side. Refresh this instance too, not
      // just the others, so it stops showing the optimistic owner from the
      // createProjectShell response.
      await refreshProjects({ silent: true });
      notifyProjectsChanged(instanceId);
      return failedMembers;
    }

    setProjects(current =>
      current.map(project =>
        project.id === projectId ? { ...project, members } : project,
      ),
    );
    return [];
  };

  const updateProject = async (
    projectId: string,
    updates: Partial<Project>,
  ) => {
    if (!isProjectPreviewMode) {
      try {
        setError(undefined);
        const response = await updateProjectApi(apiOptions, projectId, {
          name: updates.name,
          description: updates.description,
        });
        if (!response.project) {
          throw new Error('Projects API response did not include project');
        }
        setProjects(current =>
          current.map(project =>
            project.id === projectId
              ? mapApiProjectToProject(response.project!, project)
              : project,
          ),
        );
        notifyProjectsChanged(instanceId);
        return;
      } catch (err) {
        setError(PROJECTS_SAVE_ERROR);
        throw err;
      }
    }

    setProjects(current =>
      current.map(project =>
        project.id === projectId
          ? {
              ...project,
              ...updates,
              updatedAt: new Date().toISOString().slice(0, 10),
            }
          : project,
      ),
    );
  };

  const deleteProject = async (projectId: string) => {
    if (!isProjectPreviewMode) {
      try {
        setError(undefined);
        await deleteProjectById(apiOptions, projectId);
        setProjects(current =>
          current.filter(project => project.id !== projectId),
        );
        notifyProjectsChanged(instanceId);
        return;
      } catch (err) {
        setError(PROJECTS_SAVE_ERROR);
        throw err;
      }
    }
    await updateProject(projectId, { deleted: true });
  };

  return {
    error,
    loading,
    projects,
    createProjectShell,
    assignMembers,
    deleteProject,
    refreshProjects,
    updateProject,
    page,
    pageSize,
    total,
    goToPage,
    nextPage,
    prevPage,
    hasNextPage: page * pageSize < total,
    hasPrevPage: page > 1,
  };
}
